import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import s from './AcceptInvitation.module.scss'
import { Paragraph } from 'ui-kit/components/Typography/Typography'
import AuthInput from 'ui-kit/components/AuthInput/AuthInput'
import { Button } from 'ui-kit/components/Button/Button'
import { useFormik } from 'formik'
import { CircularProgress } from '@mui/material'
import * as yup from 'yup'
import { useAppDispatch, useAppSelector } from 'hooks'
import { acceptInvite } from 'api/auth'
import AuthRoutes from '../../routes'
import { REQUIRED_FIELD, TOO_LONG } from 'pages/NewAssetPage/config'
import { LengthConstrains } from 'types'

const validationSchema = yup.object().shape({
    password: yup
        .string()
        .max(LengthConstrains.L_16, TOO_LONG)
        .trim()
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            'Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character',
        )
        .required(REQUIRED_FIELD),
    confirmPassword: yup
        .string()
        .required(REQUIRED_FIELD)
        .test('passwords-match', 'Passwords must match', function (value) {
            return this.parent.password === value
        }),
})

type NewPasswordForm = {
    password: string
    confirmPassword: string
}

const AcceptInvitation = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [params] = useSearchParams()
    const token = params.get('userEmail')

    const isWaitingForResponse = useAppSelector(({ auth }) => auth.isWaitingForResponse)

    const { handleChange, values, errors, isValid, handleSubmit } = useFormik<NewPasswordForm>({
        initialValues: {
            password: '',
            confirmPassword: '',
        },

        validateOnChange: false,
        validationSchema: validationSchema,
        onSubmit: async (val) => {
            await dispatch(
                acceptInvite({
                    password: val.password,
                    emailToken: token || '',
                    callback: handleNavigateToLogin,
                }),
            )
        },
    })

    const handleNavigateToLogin = () => {
        navigate('/' + AuthRoutes.LOGIN, { replace: true })
    }

    return (
        <div className={s.contentContainer}>
            <Paragraph className={`${s.logIn} ${isWaitingForResponse ? 'opacity' : ''}`} color={'white'}>
                Create password
            </Paragraph>
            <Paragraph className={`${s.title} ${isWaitingForResponse ? 'opacity' : ''}`} color={'white'}>
                Write password for your new account and use it to login
            </Paragraph>
            <div className={s.inputsContainer}>
                <AuthInput
                    id="password"
                    name="password"
                    type="password"
                    title="Password"
                    value={values.password}
                    onChange={handleChange}
                    error={errors.password}
                    disabled={isWaitingForResponse}
                    onEnterPress={handleSubmit}
                    defaultShowError={!!errors.password}
                />
                <AuthInput
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    title="Confirm password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    error={errors.confirmPassword}
                    disabled={isWaitingForResponse}
                    onEnterPress={handleSubmit}
                    defaultShowError={!!errors.confirmPassword}
                />
            </div>

            <Button className={s.logInBtn} onClick={() => handleSubmit()} variants={'filled-yellow'}>
                {isWaitingForResponse ? (
                    <CircularProgress
                        sx={{
                            color: 'white',
                            '& .MuiCircularProgress-svg': {
                                margin: 'auto',
                                height: 40,
                                width: 20,
                            },
                        }}
                        color="inherit"
                    />
                ) : (
                    <Paragraph size={'s'} weight={'semi-bold'}>
                        Create password
                    </Paragraph>
                )}
            </Button>
        </div>
    )
}

export default AcceptInvitation
